import { isCancel } from "axios";

import { showErrorNotification } from "../../utils/notification";

/**
 * Base path for every external-problem admin endpoint, mounted under
 * `/admin/external-problems` on the backend.
 */
export const EXTERNAL_PROBLEMS_API_BASE = "admin/external-problems";

/** Page size and pager options shared by every paginated table on these screens. */
export const EXTERNAL_PROBLEMS_PAGINATION_CONFIG = Object.freeze({
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 25,
  MAX_PAGE_SIZE: 100,
  PAGE_SIZE_OPTIONS: ["10", "25", "50", "100"]
});

/** Day-level date format shown in every table on these screens. */
export const DATE_DISPLAY_FORMAT = "DD MMM YYYY";

/** Direction values the list endpoints take for `sortOrder`. */
export const SORT_ORDER_ENUM = Object.freeze({
  ASC: "asc",
  DESC: "desc"
});

/** Boolean filter values as the list endpoints take them, which is as strings. */
export const BOOLEAN_FILTER_VALUE_ENUM = Object.freeze({
  TRUE: "true",
  FALSE: "false"
});

/** Shared pixel widths for table columns, named by what the column holds. */
export const COLUMN_WIDTH_ENUM = Object.freeze({
  ICON: 80,
  ID: 110,
  SHORT: 130,
  COUNT: 140,
  DATE: 150,
  WIDE: 160,
  WIDER: 170,
  TEXT: 200,
  LONG: 220,
  LINK: 260
});

/**
 * Sums the declared widths of a table's columns. antd renders header and body
 * as two tables that each redistribute slack on their own, so a table's
 * `scroll.x` must equal this sum.
 *
 * @param {Record<string, number>} columnWidths - Pixel width per column.
 * @returns {number}
 */
export const sumColumnWidths = (columnWidths) =>
  Object.values(columnWidths).reduce((totalWidth, width) => totalWidth + width, 0);

/** Factor that turns a 0-to-1 ratio into a percentage. */
const RATIO_TO_PERCENT_FACTOR = 100;

/** Decimal places shown in every percentage on these screens. */
const PERCENT_DECIMAL_PLACES = 1;

/**
 * A 0-to-1 ratio as a percentage number, rounded to `PERCENT_DECIMAL_PLACES`.
 *
 * @param {number} ratio - 0 to 1.
 * @returns {number} 0 to 100.
 */
export const toPercentNumber = (ratio) => Number((ratio * RATIO_TO_PERCENT_FACTOR).toFixed(PERCENT_DECIMAL_PLACES));

/**
 * A 0-to-1 ratio as percentage text, always showing `PERCENT_DECIMAL_PLACES` decimals.
 *
 * @param {number} ratio - 0 to 1.
 * @returns {string} For example `"12.5%"`.
 */
export const formatPercent = (ratio) => `${(ratio * RATIO_TO_PERCENT_FACTOR).toFixed(PERCENT_DECIMAL_PLACES)}%`;

/**
 * Mirrors the backend's `PROBLEM_SOURCE_ENUM`
 * (`backend-service/src/models/_utils/enums.js`). Every value the `platform`
 * field on a problem or a request row can carry, including the judges the
 * catalogue holds no data for.
 */
export const PLATFORM_ENUM = Object.freeze({
  CODEFORCES_PROBLEM: "CODEFORCES_PROBLEM",
  LEETCODE_PROBLEM: "LEETCODE_PROBLEM",
  ATCODER_PROBLEM: "ATCODER_PROBLEM",
  CODECHEF_PROBLEM: "CODECHEF_PROBLEM",
  HACKEREARTH_PROBLEM: "HACKEREARTH_PROBLEM",
  CSES_PROBLEM: "CSES_PROBLEM",
  GFG_PROBLEM: "GFG_PROBLEM",
  HACKERRANK_PROBLEM: "HACKERRANK_PROBLEM",
  SPOJ_PROBLEM: "SPOJ_PROBLEM",
  OTHER_WITH_LINK: "OTHER_WITH_LINK",
  OTHER: "OTHER"
});

/**
 * The nine judges the catalogue stores discriminator documents for.
 * A create/edit form only ever renders fields for one of these; every other
 * platform value can appear on a request row but has no field shape to submit
 * against `POST /admin/external-problems`.
 */
export const CATALOGUED_PLATFORMS = Object.freeze([
  PLATFORM_ENUM.CODEFORCES_PROBLEM,
  PLATFORM_ENUM.ATCODER_PROBLEM,
  PLATFORM_ENUM.CODECHEF_PROBLEM,
  PLATFORM_ENUM.HACKEREARTH_PROBLEM,
  PLATFORM_ENUM.CSES_PROBLEM,
  PLATFORM_ENUM.LEETCODE_PROBLEM,
  PLATFORM_ENUM.SPOJ_PROBLEM,
  PLATFORM_ENUM.GFG_PROBLEM,
  PLATFORM_ENUM.HACKERRANK_PROBLEM
]);

/** Display label for every platform value, used to build filter dropdowns. */
export const PLATFORM_DISPLAY_NAME_MAP = Object.freeze({
  [PLATFORM_ENUM.CODEFORCES_PROBLEM]: "Codeforces",
  [PLATFORM_ENUM.LEETCODE_PROBLEM]: "LeetCode",
  [PLATFORM_ENUM.ATCODER_PROBLEM]: "AtCoder",
  [PLATFORM_ENUM.CODECHEF_PROBLEM]: "CodeChef",
  [PLATFORM_ENUM.HACKEREARTH_PROBLEM]: "HackerEarth",
  [PLATFORM_ENUM.CSES_PROBLEM]: "CSES",
  [PLATFORM_ENUM.GFG_PROBLEM]: "GeeksforGeeks",
  [PLATFORM_ENUM.HACKERRANK_PROBLEM]: "HackerRank",
  [PLATFORM_ENUM.SPOJ_PROBLEM]: "SPOJ",
  [PLATFORM_ENUM.OTHER_WITH_LINK]: "Other (with link)",
  [PLATFORM_ENUM.OTHER]: "Other"
});

/**
 * Builds select options for a list of platform values, labelled with each
 * platform's display name.
 *
 * @param {ReadonlyArray<string>} platforms - Values from `PLATFORM_ENUM`.
 * @returns {Array<{label: string, value: string}>}
 */
const buildPlatformOptions = (platforms) =>
  platforms.map((platform) => ({
    label: PLATFORM_DISPLAY_NAME_MAP[platform],
    value: platform
  }));

export const ALL_PLATFORM_FILTER_OPTIONS = buildPlatformOptions(Object.values(PLATFORM_ENUM));

/**
 * Judges the backend's registry knows, mirroring `PLATFORM_REGISTRY` in
 * `src/lib/external-problems/platform-registry.js`.
 *
 * Listed outright, so a new `PROBLEM_SOURCE_ENUM` value reaches the filters
 * only when it is added here. An endpoint validating `platform` against the
 * registry rejects every value the registry lacks.
 */
export const REGISTERED_PLATFORMS = Object.freeze([...CATALOGUED_PLATFORMS]);

export const REGISTERED_PLATFORM_FILTER_OPTIONS = buildPlatformOptions(REGISTERED_PLATFORMS);

export const CATALOGUED_PLATFORM_OPTIONS = buildPlatformOptions(CATALOGUED_PLATFORMS);

/** Help text used on more than one screen. */
export const SHARED_INFO_TEXT = Object.freeze({
  PLATFORM:
    "The judge a problem comes from, such as Codeforces or LeetCode. The help text on these screens calls it the judge; they mean the same thing.",
  DISTINCT_REQUESTER_COUNT:
    "How many different student accounts asked for this problem. One account asking many times only counts once, so this can't be inflated by repeat asks."
});

/** Values `GET /requests` takes for `sortBy`. */
export const QUEUE_SORT_BY_ENUM = Object.freeze({
  RECENTLY_REQUESTED: "recentlyRequested",
  DISTINCT_REQUESTERS: "distinctRequesters"
});

/** Minimum distinct-student counts offered by the queue filter. */
export const MIN_DISTINCT_REQUESTER_OPTIONS = Object.freeze(
  [2, 3, 5, 10].map((count) => ({ label: `${count}+`, value: count }))
);

/** Mirrors the backend's `EXTERNAL_PROBLEM_REQUEST_STATUS`. */
export const REQUEST_STATUS_ENUM = Object.freeze({
  PENDING_REVIEW: "PENDING_REVIEW",
  INCOMPLETE_DATA: "INCOMPLETE_DATA",
  UNSUPPORTED_PLATFORM: "UNSUPPORTED_PLATFORM",
  UNAVAILABLE: "UNAVAILABLE",
  FULFILLED: "FULFILLED",
  REJECTED_INVALID: "REJECTED_INVALID"
});

/** Badge color, ops-facing label, and one-sentence meaning for every request status. */
export const REQUEST_STATUS_DISPLAY_CONFIG = Object.freeze({
  [REQUEST_STATUS_ENUM.PENDING_REVIEW]: {
    label: "Pending review",
    color: "orange",
    helpText: "Students asked for this problem and we don't hold it. Create it, or rule the link out."
  },
  [REQUEST_STATUS_ENUM.INCOMPLETE_DATA]: {
    label: "Incomplete data",
    color: "gold",
    helpText: "We hold this problem, but it has no usable statement yet. Edit it to add one."
  },
  [REQUEST_STATUS_ENUM.UNSUPPORTED_PLATFORM]: {
    label: "Unsupported platform",
    color: "purple",
    helpText: "We recognise the site, but the catalogue holds no data for it at all yet."
  },
  [REQUEST_STATUS_ENUM.UNAVAILABLE]: {
    label: "Unavailable",
    color: "volcano",
    helpText: "Real, but we can't store a usable statement for it: the statement is published only as images, or the problem has been withdrawn."
  },
  [REQUEST_STATUS_ENUM.FULFILLED]: {
    label: "Fulfilled",
    color: "green",
    helpText: "Populated and the student's ask is answered. Nothing left to do."
  },
  [REQUEST_STATUS_ENUM.REJECTED_INVALID]: {
    label: "Rejected, invalid",
    color: "red",
    helpText: "An admin ruled that this link does not name a real problem."
  }
});

/**
 * Tab strip config for the reconciliation queue. Each tab names the request
 * statuses it covers and how its row count is read off `statusCounts`.
 *
 * `UNRESOLVED` and `CLOSED_WITHOUT_FIX` span more than one status; their
 * `statuses` array is joined into the comma-separated form `GET /requests`
 * accepts for its `status` query param, so every tab resolves to one
 * server-paginated request regardless of how many statuses it covers.
 *
 * `ALL` lists every status explicitly. The endpoint defaults an omitted
 * `status` to the unresolved set, so the list is what makes the tab show all.
 */
export const QUEUE_TABS = Object.freeze([
  {
    key: "UNRESOLVED",
    label: "Unresolved",
    description: "Rows a human still has to act on: create the problem, or fill in a statement.",
    statuses: [
      REQUEST_STATUS_ENUM.PENDING_REVIEW,
      REQUEST_STATUS_ENUM.INCOMPLETE_DATA
    ]
  },
  {
    key: "UNSUPPORTED_PLATFORM",
    label: "Unsupported platform",
    description: "We recognise the site but hold no data for it at all. There is no per-row action; the count is the evidence for adding the judge.",
    statuses: [REQUEST_STATUS_ENUM.UNSUPPORTED_PLATFORM]
  },
  {
    key: "FULFILLED",
    label: "Fulfilled",
    description: "The problem is populated and the student's ask is answered. Nothing left to do.",
    statuses: [REQUEST_STATUS_ENUM.FULFILLED]
  },
  {
    key: "CLOSED_WITHOUT_FIX",
    label: "Closed without fix",
    description: "An admin ruled the link is not a real problem, or that it is real but we can never store a usable statement for it.",
    statuses: [REQUEST_STATUS_ENUM.REJECTED_INVALID, REQUEST_STATUS_ENUM.UNAVAILABLE]
  },
  {
    key: "ALL",
    label: "All",
    description: "Every request ever created, regardless of status.",
    statuses: Object.values(REQUEST_STATUS_ENUM)
  }
]);

/** The three review decisions ops may apply by hand to a queue row. */
export const REVIEW_ACTION_ENUM = Object.freeze({
  REJECT_INVALID: REQUEST_STATUS_ENUM.REJECTED_INVALID,
  MARK_UNAVAILABLE: REQUEST_STATUS_ENUM.UNAVAILABLE,
  REOPEN: REQUEST_STATUS_ENUM.PENDING_REVIEW
});

/** Copy distinguishing "this link is not a real problem" from "it is real but can't be served". */
export const REVIEW_ACTION_CONFIG = Object.freeze({
  [REVIEW_ACTION_ENUM.REJECT_INVALID]: {
    label: "Reject as invalid",
    helpText: "The link does not name a real problem on this judge."
  },
  [REVIEW_ACTION_ENUM.MARK_UNAVAILABLE]: {
    label: "Mark unavailable",
    helpText:
      "Real, but we can't store a usable statement for it: the statement is published only as images, or the problem has been withdrawn."
  },
  [REVIEW_ACTION_ENUM.REOPEN]: {
    label: "Reopen for review",
    helpText: "Put this row back in front of an admin as unresolved work."
  }
});

/**
 * Mirrors the backend's `EXTERNAL_PROBLEM_CONTENT_FIDELITY`: how complete a
 * problem's stored data is, from bare metadata up to a full statement, or
 * manually verified by an admin. Ingestion never lowers this tier once a
 * problem has reached it.
 */
export const CONTENT_COMPLETENESS_ENUM = Object.freeze({
  METADATA_ONLY: 0,
  PLAIN_STATEMENT: 1,
  FULL_MARKUP: 2,
  MANUALLY_CURATED: 3
});

/** Readable label and badge color for each content-completeness tier. */
export const CONTENT_COMPLETENESS_DISPLAY_CONFIG = Object.freeze({
  [CONTENT_COMPLETENESS_ENUM.METADATA_ONLY]: { label: "Metadata only", color: "default" },
  [CONTENT_COMPLETENESS_ENUM.PLAIN_STATEMENT]: { label: "Plain statement", color: "blue" },
  [CONTENT_COMPLETENESS_ENUM.FULL_MARKUP]: { label: "Full markup", color: "cyan" },
  [CONTENT_COMPLETENESS_ENUM.MANUALLY_CURATED]: { label: "Manually curated", color: "green" }
});

/** Label and badge colour for each of the two ways a problem's details got into the catalogue. */
export const DETAIL_ORIGIN_DISPLAY_CONFIG = Object.freeze({
  ADMIN_ENTERED: { label: "Admin-entered", color: "green" },
  IMPORTED: { label: "Imported", color: "default" }
});

/** The detail-origin filter, whose values are the `isManuallyCurated` strings the endpoint takes. */
export const DETAIL_ORIGIN_FILTER_OPTIONS = Object.freeze([
  { label: DETAIL_ORIGIN_DISPLAY_CONFIG.ADMIN_ENTERED.label, value: BOOLEAN_FILTER_VALUE_ENUM.TRUE },
  { label: DETAIL_ORIGIN_DISPLAY_CONFIG.IMPORTED.label, value: BOOLEAN_FILTER_VALUE_ENUM.FALSE }
]);

/** The sample-test-case filter, whose values are the `hasSamples` strings the endpoint takes. */
export const SAMPLE_TEST_CASES_FILTER_OPTIONS = Object.freeze([
  { label: "Stored", value: BOOLEAN_FILTER_VALUE_ENUM.TRUE },
  { label: "None stored", value: BOOLEAN_FILTER_VALUE_ENUM.FALSE }
]);

/** Select options for the content-completeness filter, valued by tier. */
export const CONTENT_COMPLETENESS_FILTER_OPTIONS = Object.freeze(
  Object.entries(CONTENT_COMPLETENESS_DISPLAY_CONFIG).map(([contentFidelity, { label }]) => ({
    label,
    value: contentFidelity
  }))
);

/** Help text for the Catalogue filters and columns and for the create and edit form. */
export const CATALOGUE_INFO_TEXT = Object.freeze({
  CONTENT_COMPLETENESS:
    "How complete this problem's stored data is: metadata only (title and tags, no statement), a plain statement, full markup with samples and limits, or manually verified by an admin. A tier is never lowered by automated ingestion once it's been reached.",
  DETAIL_ORIGIN:
    "Where this problem's stored details came from. \"Admin-entered\" means an admin typed or corrected them by hand, which also exempts the row from every later automated import. \"Imported\" means every field arrived from a bulk dataset or a read of the judge, and a later import may still update it. Editing any content field (statement, constraints, samples, tags, rating, limits) turns a row admin-entered; editing only the title does not.",
  SAMPLE_TEST_CASES:
    "The worked input and output pairs printed alongside the problem statement, stored so an answer can point at a concrete case. \"None stored\" covers a row whose samples are empty and one that never carried the field at all, which is every LeetCode row: LeetCode keeps its examples under test cases instead, so this filter says nothing useful about that judge and is hidden when it is selected.",
  TAG_FILTER:
    "Our own topic names, offered from the tags the catalogue's own problems actually carry, narrowed to the selected platform. Picking several keeps only problems carrying every one of them.",
  DIFFICULTY_FILTER:
    "The difficulty values this platform's own problems carry. A judge that grades on a number offers its rating buckets and a rating range alongside them; a judge that grades on a word, like LeetCode, offers those words instead. Nothing is offered that no stored problem carries.",
  CREATE_FROM_URL:
    "Paste the problem's page address. We read the platform and the problem's own identifier straight out of it, which is exactly how a student's pasted link is matched later, so a record created this way can always be found again."
});

/** The `parseStatus` the backend gives a link on a judge the catalogue stores nothing for. */
export const UNSUPPORTED_PLATFORM_PARSE_STATUS = "UNSUPPORTED_PLATFORM";

/**
 * Why a pasted link yielded nothing to create, in the words an admin needs to
 * decide what to do next. Keyed by the backend's
 * `PARSED_PROBLEM_URL_REJECTION_REASON`, plus `UNSUPPORTED_PLATFORM` for a
 * link on a judge the catalogue deliberately stores nothing for.
 */
export const URL_REJECTION_MESSAGE_MAP = Object.freeze({
  NOT_A_URL: "That is not a web link. Paste the problem's page address, starting with https://.",
  UNKNOWN_HOST: "We don't recognise that site. Only the judges listed in the platform filter can be catalogued.",
  NOT_A_PROBLEM_PATH:
    "That link is on a judge we know, but it doesn't point at a single problem. Open the problem itself and copy the address from there.",
  MALFORMED_ID:
    "The problem identifier in that link doesn't match what this judge uses, so nothing would ever resolve against it.",
  UNSUPPORTED_PLATFORM:
    "We recognise that judge but hold no problems for it, so there is no record to create here. Student requests for it are still counted on the Reconciliation Queue."
});

/**
 * Field labels shared by every judge platform. Every catalogued platform
 * accepts this set on top of its own extra fields below.
 */
export const BASE_PROBLEM_FIELD_LABELS = Object.freeze({
  title: "Title",
  tags: "Tags",
  sourceTags: "Source tags",
  externalUrl: "External URL",
  statementMarkdown: "Statement",
  editorialMarkdown: "Editorial",
  constraintsMarkdown: "Constraints",
  referenceSolutions: "Reference solutions"
});

/** Execution-limit and statement-section fields shared by the judge and CSES field sets. */
const EXECUTION_AND_FORMAT_FIELD_LABELS = Object.freeze({
  timeLimitMs: "Time limit (ms)",
  memoryLimitKb: "Memory limit (KB)",
  inputFormatMarkdown: "Input format",
  outputFormatMarkdown: "Output format",
  samples: "Samples"
});

/** Fields shared by every judge that groups its problems into contests, in render order. */
const CONTEST_PROBLEM_FIELD_LABELS = Object.freeze({
  rating: "Rating",
  contestId: "Contest ID",
  contestName: "Contest name",
  ...EXECUTION_AND_FORMAT_FIELD_LABELS,
  notesMarkdown: "Notes"
});

/** Fields shared by the hand-curated judges that publish neither a rating nor a contest, in render order. */
const HAND_CURATED_PROBLEM_FIELD_LABELS = Object.freeze({
  ...EXECUTION_AND_FORMAT_FIELD_LABELS,
  notesMarkdown: "Notes"
});

/** Extra fields beyond the base set, keyed by platform, in the order the form renders them. */
export const PLATFORM_EXTRA_FIELD_LABELS = Object.freeze({
  [PLATFORM_ENUM.CODEFORCES_PROBLEM]: CONTEST_PROBLEM_FIELD_LABELS,
  [PLATFORM_ENUM.ATCODER_PROBLEM]: CONTEST_PROBLEM_FIELD_LABELS,
  [PLATFORM_ENUM.CODECHEF_PROBLEM]: CONTEST_PROBLEM_FIELD_LABELS,
  [PLATFORM_ENUM.HACKEREARTH_PROBLEM]: CONTEST_PROBLEM_FIELD_LABELS,
  [PLATFORM_ENUM.CSES_PROBLEM]: EXECUTION_AND_FORMAT_FIELD_LABELS,
  [PLATFORM_ENUM.SPOJ_PROBLEM]: HAND_CURATED_PROBLEM_FIELD_LABELS,
  [PLATFORM_ENUM.GFG_PROBLEM]: {
    difficultyLabel: "Difficulty",
    ...HAND_CURATED_PROBLEM_FIELD_LABELS
  },
  [PLATFORM_ENUM.HACKERRANK_PROBLEM]: {
    difficultyLabel: "Difficulty",
    ...HAND_CURATED_PROBLEM_FIELD_LABELS
  },
  [PLATFORM_ENUM.LEETCODE_PROBLEM]: {
    difficultyLabel: "Difficulty",
    testCases: "Test cases",
    hints: "Hints",
    starterCode: "Starter code"
  }
});

/** Difficulty enum for LeetCode and HackerRank. LeetCode requires it on create; HackerRank does not. */
export const LEETCODE_DIFFICULTY_OPTIONS = Object.freeze([
  { label: "Easy", value: "EASY" },
  { label: "Medium", value: "MEDIUM" },
  { label: "Hard", value: "HARD" }
]);

/** GeeksforGeeks grades two tiers below Easy, so its difficulty select offers them first. */
export const GFG_DIFFICULTY_OPTIONS = Object.freeze([
  { label: "School", value: "SCHOOL" },
  { label: "Basic", value: "BASIC" },
  ...LEETCODE_DIFFICULTY_OPTIONS
]);

/**
 * Every field name a given platform's form may submit, base fields first.
 *
 * @param {string} platform - One of `CATALOGUED_PLATFORMS`.
 * @returns {string[]} Field names in render order.
 */
export const getFieldNamesForPlatform = (platform) => [
  ...Object.keys(BASE_PROBLEM_FIELD_LABELS),
  ...Object.keys(PLATFORM_EXTRA_FIELD_LABELS[platform] ?? {})
];

/**
 * The label a form should show for one of a platform's fields.
 *
 * @param {string} platform - One of `CATALOGUED_PLATFORMS`.
 * @param {string} fieldName
 * @returns {string}
 */
export const getFieldLabel = (platform, fieldName) =>
  BASE_PROBLEM_FIELD_LABELS[fieldName] ?? PLATFORM_EXTRA_FIELD_LABELS[platform]?.[fieldName] ?? fieldName;

/** Fields the form renders as a free-text tag input, one string per tag. */
export const TAG_ARRAY_FIELD_NAMES = Object.freeze(["tags", "sourceTags", "hints"]);

/** Fields the form renders as a plain number input. */
export const NUMBER_FIELD_NAMES = Object.freeze(["rating", "timeLimitMs", "memoryLimitKb"]);

/** Long-form fields rendered as a multi-row text area. */
export const LONG_TEXT_FIELD_NAMES = Object.freeze([
  "statementMarkdown",
  "editorialMarkdown",
  "constraintsMarkdown",
  "notesMarkdown",
  "inputFormatMarkdown",
  "outputFormatMarkdown",
  "starterCode"
]);

/** Fields rendered as a fixed-choice select, with the options to offer. */
export const SELECT_FIELD_OPTIONS = Object.freeze({
  difficultyLabel: LEETCODE_DIFFICULTY_OPTIONS
});

/** Select options a platform replaces the shared ones with, keyed by platform then field name. */
const PLATFORM_SELECT_FIELD_OPTIONS = Object.freeze({
  [PLATFORM_ENUM.GFG_PROBLEM]: {
    difficultyLabel: GFG_DIFFICULTY_OPTIONS
  }
});

/**
 * The options a fixed-choice field offers on one platform's form.
 *
 * @param {string} platform - One of `CATALOGUED_PLATFORMS`.
 * @param {string} fieldName - A key of `SELECT_FIELD_OPTIONS`.
 * @returns {Array<{label: string, value: string}>}
 */
export const getSelectFieldOptions = (platform, fieldName) =>
  PLATFORM_SELECT_FIELD_OPTIONS[platform]?.[fieldName] ?? SELECT_FIELD_OPTIONS[fieldName];

/** Input types a repeatable field's sub-field can render. */
export const SUB_FIELD_TYPE_ENUM = Object.freeze({
  INPUT: "input",
  TEXTAREA: "textarea"
});

/**
 * Repeatable sub-document fields, and the inputs each row needs.
 *
 * `optional: true` on a sub-field means the row is still valid without it
 * (`samples[].explanation`, which the base schema itself marks optional).
 */
export const REPEATABLE_FIELD_CONFIG = Object.freeze({
  referenceSolutions: {
    addButtonLabel: "Add reference solution",
    subFields: [
      { name: "language", label: "Language", type: SUB_FIELD_TYPE_ENUM.INPUT },
      { name: "solution", label: "Solution", type: SUB_FIELD_TYPE_ENUM.TEXTAREA }
    ]
  },
  testCases: {
    addButtonLabel: "Add test case",
    subFields: [
      { name: "input", label: "Input", type: SUB_FIELD_TYPE_ENUM.TEXTAREA },
      { name: "output", label: "Output", type: SUB_FIELD_TYPE_ENUM.TEXTAREA }
    ]
  },
  samples: {
    addButtonLabel: "Add sample",
    subFields: [
      { name: "input", label: "Input", type: SUB_FIELD_TYPE_ENUM.TEXTAREA },
      { name: "output", label: "Output", type: SUB_FIELD_TYPE_ENUM.TEXTAREA },
      { name: "explanation", label: "Explanation", type: SUB_FIELD_TYPE_ENUM.TEXTAREA, optional: true }
    ]
  }
});

/** The render strategies a form field can take. */
export const FIELD_RENDER_KIND_ENUM = Object.freeze({
  TAG_ARRAY: "tagArray",
  NUMBER: "number",
  SELECT: "select",
  REPEATABLE: "repeatable",
  LONG_TEXT: "longText",
  SHORT_TEXT: "shortText"
});

/**
 * Field names per render kind, checked in order: the first kind listing a
 * field name wins, and a name no kind lists renders as a single-line input.
 */
const FIELD_RENDER_KIND_ENTRIES = Object.freeze([
  [FIELD_RENDER_KIND_ENUM.TAG_ARRAY, TAG_ARRAY_FIELD_NAMES],
  [FIELD_RENDER_KIND_ENUM.NUMBER, NUMBER_FIELD_NAMES],
  [FIELD_RENDER_KIND_ENUM.SELECT, Object.keys(SELECT_FIELD_OPTIONS)],
  [FIELD_RENDER_KIND_ENUM.REPEATABLE, Object.keys(REPEATABLE_FIELD_CONFIG)],
  [FIELD_RENDER_KIND_ENUM.LONG_TEXT, LONG_TEXT_FIELD_NAMES]
]);

/**
 * Which render strategy a field name falls under, so the form can pick a
 * component for it without a condition chain at each call site.
 *
 * @param {string} fieldName
 * @returns {"tagArray"|"number"|"select"|"repeatable"|"longText"|"shortText"} Fields
 *   with no more specific match default to a plain single-line input.
 */
export const getFieldRenderKind = (fieldName) => {
  const [renderKind = FIELD_RENDER_KIND_ENUM.SHORT_TEXT] =
    FIELD_RENDER_KIND_ENTRIES.find(([, fieldNames]) => fieldNames.includes(fieldName)) ?? [];

  return renderKind;
};

/** Height in pixels of each chart on the Trend card. */
export const ANALYTICS_CHART_HEIGHT = 280;

/** How far back telemetry retains data; the backend clamps any older start date to this. */
export const ANALYTICS_RETENTION_DAYS = 365;

/** Width of the default date-range window the Analytics subtab opens with. */
export const ANALYTICS_DEFAULT_WINDOW_DAYS = 30;

/**
 * The five resolutions every telemetry counter and chart series in the Analytics
 * subtab breaks a request down into. One color per resolution, reused across the
 * trend chart, the platform table and every badge, so the same shade always
 * means the same resolution.
 */
export const ANALYTICS_RESOLUTION_ENUM = Object.freeze({
  RESOLVED: "resolved",
  PARTIAL: "partial",
  NOT_FOUND: "notFound",
  INVALID_URL: "invalidUrl",
  UNSUPPORTED_PLATFORM: "unsupportedPlatform"
});

/**
 * Display label, antd tag color, chart hex, the `totals`/`platforms`/`trend`
 * field each resolution reads its count from, and one plain sentence explaining
 * what the resolution means, for the tables that show it as a column.
 */
export const ANALYTICS_RESOLUTION_DISPLAY_CONFIG = Object.freeze({
  [ANALYTICS_RESOLUTION_ENUM.RESOLVED]: {
    label: "Resolved",
    color: "green",
    hex: "#52C41A",
    countField: "resolvedCount",
    infoText:
      "The pasted link matched a problem we hold, with a statement good enough to answer from."
  },
  [ANALYTICS_RESOLUTION_ENUM.PARTIAL]: {
    label: "Partial",
    color: "gold",
    hex: "#FAAD14",
    countField: "partialCount",
    infoText:
      "We hold the problem but not a usable statement, so the model got nothing to answer from. Counts as a miss."
  },
  [ANALYTICS_RESOLUTION_ENUM.NOT_FOUND]: {
    label: "Not found",
    color: "volcano",
    hex: "#FA541C",
    countField: "notFoundCount",
    infoText:
      "A real problem link on a platform we cover, for a problem we hold nothing for. This is the gap the reconciliation queue exists to close."
  },
  [ANALYTICS_RESOLUTION_ENUM.INVALID_URL]: {
    label: "Invalid URL",
    color: "red",
    hex: "#F5222D",
    countField: "invalidUrlCount",
    infoText:
      "The pasted text was not a problem link at all, so there was nothing to look up. Left out of the hit ratio."
  },
  [ANALYTICS_RESOLUTION_ENUM.UNSUPPORTED_PLATFORM]: {
    label: "Unsupported platform",
    color: "purple",
    hex: "#722ED1",
    countField: "unsupportedPlatformCount",
    infoText:
      "The link pointed at a site the platform list does not cover, so no lookup was possible. Left out of the hit ratio."
  }
});

/** Accent color for the hit-ratio line, deliberately outside the resolution palette above. */
export const ANALYTICS_HIT_RATIO_COLOR = "#1677FF";

/** Help text for the Analytics cards, columns, filters and the per-student table. */
export const ANALYTICS_INFO_TEXT = Object.freeze({
  ATTEMPT_COUNT:
    "Every problem link a student pasted, counted once per paste. The same link pasted twice counts twice.",
  HIT_RATIO:
    "Resolved / (Resolved + Partial + Not found). Invalid URLs and unsupported platforms are deliberately excluded, so a run of mistyped links doesn't read as the catalogue getting worse.",
  DISTINCT_STUDENT_COUNT:
    "How many different student accounts pasted at least one problem link. A student pasting fifty links still counts once.",
  PLATFORM_FILTER_SCOPE:
    "Narrows the headline counters and the per-platform table to one platform. The queue counters below always cover every platform.",
  CATALOGUE_COLUMN:
    "A platform showing 'Not catalogued' and a zero hit ratio is correct and expected, not an error: it means we hold nothing for that platform by choice.",
  QUEUE_OPEN:
    "What the reconciliation queue still owes ops, across every judge. This does not narrow to the platform filter above: it always counts the whole outstanding workload.",
  QUEUE_STUDENTS_WAITING:
    "Different students waiting on at least one open request, across every platform rather than the one selected above.",
  QUEUE_FULFILLED:
    "Requests that got a usable statement and left the queue inside the selected dates, across every platform rather than the one selected above.",
  PLATFORM_BREAKDOWN:
    "One row per platform students actually pasted a link for. A platform nobody asked about is left out rather than listed at zero.",
  DELETED_ACCOUNT:
    "The student account has been removed, so there is no name or email left to show. The row stays because the activity behind it happened inside the selected dates; the account's internal id is all we still hold.",
  INVALID_URL_SHARE:
    "Share of this student's pastes that weren't a problem link at all. High alongside a high attempt count is what distinguishes abuse from a student the catalogue is failing.",
  STUDENT_PLATFORMS:
    "Every platform this student pasted a link for in this window. A link on a site we don't recognise at all is left out, so a student can show fewer tags than attempts.",
  RESOLUTION_FILTER:
    "Keeps students who hit this resolution at least once, and still shows each of them their whole mix of resolutions. It answers who the catalogue is failing, not how often."
});

/** The backend's `EXTERNAL_PROBLEM_RESOLUTION_STATUS` value for each analytics resolution key. */
const RESOLUTION_FILTER_VALUE_MAP = Object.freeze({
  [ANALYTICS_RESOLUTION_ENUM.RESOLVED]: "RESOLVED",
  [ANALYTICS_RESOLUTION_ENUM.PARTIAL]: "PARTIAL",
  [ANALYTICS_RESOLUTION_ENUM.NOT_FOUND]: "NOT_FOUND",
  [ANALYTICS_RESOLUTION_ENUM.INVALID_URL]: "INVALID_URL",
  [ANALYTICS_RESOLUTION_ENUM.UNSUPPORTED_PLATFORM]: "UNSUPPORTED_PLATFORM"
});

/**
 * Mirrors the backend's `EXTERNAL_PROBLEM_RESOLUTION_STATUS`, for the
 * resolution filter on the Trend chart and the Per-Student table.
 * `NOT_ATTEMPTED` is deliberately excluded: it marks a telemetry row that
 * never reached a resolution at all, not a resolution worth filtering by.
 */
export const RESOLUTION_FILTER_OPTIONS = Object.freeze(
  Object.entries(RESOLUTION_FILTER_VALUE_MAP).map(([resolutionKey, filterValue]) => ({
    label: ANALYTICS_RESOLUTION_DISPLAY_CONFIG[resolutionKey].label,
    value: filterValue
  }))
);

/** Mirrors the backend's `sortBy` enum for `GET /analytics/students`. */
export const STUDENT_SORT_BY_ENUM = Object.freeze({
  ATTEMPT_COUNT: "attemptCount",
  RESOLVED_COUNT: "resolvedCount",
  PARTIAL_COUNT: "partialCount",
  NOT_FOUND_COUNT: "notFoundCount",
  INVALID_URL_COUNT: "invalidUrlCount",
  UNSUPPORTED_PLATFORM_COUNT: "unsupportedPlatformCount",
  HIT_RATIO: "hitRatio",
  LAST_ATTEMPTED_AT: "lastAttemptedAt"
});

/**
 * Maps an antd Table column key to the `sortBy` value it drives. A column
 * absent from this map is not sortable, so the table never asks the endpoint
 * for an ordering it would reject.
 */
export const STUDENT_TABLE_SORT_COLUMN_MAP = Object.freeze({
  attemptCount: STUDENT_SORT_BY_ENUM.ATTEMPT_COUNT,
  resolvedCount: STUDENT_SORT_BY_ENUM.RESOLVED_COUNT,
  partialCount: STUDENT_SORT_BY_ENUM.PARTIAL_COUNT,
  notFoundCount: STUDENT_SORT_BY_ENUM.NOT_FOUND_COUNT,
  invalidUrlCount: STUDENT_SORT_BY_ENUM.INVALID_URL_COUNT,
  hitRatio: STUDENT_SORT_BY_ENUM.HIT_RATIO,
  lastAttemptedAt: STUDENT_SORT_BY_ENUM.LAST_ATTEMPTED_AT
});

/** antd's sorter order translated into the `sortOrder` param the endpoint takes. */
export const STUDENT_SORT_ORDER_BY_TABLE_ORDER = Object.freeze({
  ascend: SORT_ORDER_ENUM.ASC,
  descend: SORT_ORDER_ENUM.DESC
});

/**
 * A percentage from a 0-to-1 ratio, rounded to one decimal, with a dash for a
 * judge the catalogue holds nothing for rather than a misleading "0%".
 *
 * @param {number} ratio - 0 to 1.
 * @param {boolean} hasCatalogueData - False when `answerableCount` is 0 by decision.
 * @returns {string}
 */
export const formatHitRatioPercent = (ratio, hasCatalogueData = true) => {
  if (!hasCatalogueData) {
    return "--";
  }
  return formatPercent(ratio);
};

/**
 * Starts a request that supersedes the previous one held in the same ref: the
 * previous request is aborted and a fresh controller replaces it.
 *
 * @param {{current: AbortController|null}} abortControllerRef - Holds the latest request's controller.
 * @returns {AbortController} Its `signal` goes on the request.
 */
export const beginLatestRequest = (abortControllerRef) => {
  abortControllerRef.current?.abort();
  const abortController = new AbortController();
  abortControllerRef.current = abortController;
  return abortController;
};

/**
 * Aborts the request held in a ref, for a component's unmount cleanup.
 *
 * @param {{current: AbortController|null}} abortControllerRef
 */
export const abortLatestRequest = (abortControllerRef) => abortControllerRef.current?.abort();

/**
 * Reports a failed request once. A request aborted by a newer one is
 * superseded, not failed, and stays silent.
 *
 * @param {Error} error
 */
export const reportUnlessCancelled = (error) => {
  if (isCancel(error)) {
    return;
  }

  showErrorNotification(error);
};
